import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) setErr("This link is missing its token. Request a new reset email.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 2200);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Could not reset password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="reset-password-page">
      <div className="eyebrow">Account</div>
      <h1 className="h-display text-4xl mt-2">Reset password.</h1>

      {done ? (
        <div className="mt-8 border border-[var(--line)] bg-[var(--paper-2)] p-6 rounded-md" data-testid="reset-password-success">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 mt-0.5 text-[var(--terra)]" />
            <div>
              <div className="font-semibold">Password updated.</div>
              <p className="text-sm text-[var(--ink-soft)] mt-1">Redirecting you to sign in…</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-[var(--ink-soft)] mt-2">Pick a new password. Minimum 8 characters.</p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="reset-password-form">
            <div>
              <label className="eyebrow block mb-1.5">New password</label>
              <input
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full px-4 py-3 border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-md outline-none focus:border-[var(--ink)]"
                data-testid="reset-password-new"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Confirm new password</label>
              <input
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full px-4 py-3 border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-md outline-none focus:border-[var(--ink)]"
                data-testid="reset-password-confirm"
              />
            </div>
            {err && <div className="text-xs text-[var(--crimson)] font-mono" data-testid="reset-password-error">{err}</div>}
            <button
              disabled={busy || !token}
              type="submit"
              className="btn-primary w-full justify-center disabled:bg-[var(--ink-muted)]"
              data-testid="reset-password-submit"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        </>
      )}

      <div className="mt-6 text-sm text-[var(--ink-soft)]">
        <Link to="/login" className="inline-flex items-center gap-1 text-[var(--terra)] font-medium link-line">
          <ArrowLeft className="h-3 w-3" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
